import React, { FC, useEffect, useMemo, useState } from 'react';
import { Button, Checkbox, Modal, Table } from 'antd';
import { CheckboxValueType } from 'antd/es/checkbox/Group';
import { SearchInput } from 'common/components';
import { DEFAULT_VALUE_0 } from 'common/config';

interface IComponentProps {
  open: boolean;
  loading: boolean;
  brandList: string[];
  defaultValue?: string[];
  onClose: () => void;
  onSave: (value: string[] | null, onSuccess: () => void) => void;
}

export const ContractBrandsModal: FC<IComponentProps> = (props) => {
  const { open, loading, brandList, defaultValue, onClose, onSave } = props;

  const [search, setSearch] = useState<string>('');
  const [value, setValue] = useState<CheckboxValueType[]>([]);

  const checkAll = brandList.length === value.length;
  const indeterminate = value.length > DEFAULT_VALUE_0 && value.length < brandList.length;
  const listForView = useMemo(() => {
    return brandList.filter((brand) => brand.toLowerCase().includes(search.toLowerCase()));
  }, [brandList, search]);

  const onCheckAllChange = (checked: boolean) => {
    checked ? setValue(brandList) : setValue([]);
  };

  const onChange = (checked: boolean, value: string) => {
    if (checked) {
      setValue((prev) => [...prev, value]);
    } else {
      setValue((prev) => prev.filter((item) => item !== value));
    }
  };

  const handleClose = () => {
    if (loading) {
      return;
    }

    setSearch('');
    onClose();
  };

  const handleSave = () => {
    if (checkAll) {
      onSave(null, handleClose);
    } else {
      onSave(value as string[], handleClose);
    }
  };

  useEffect(() => {
    if (defaultValue) {
      setValue(defaultValue);
    } else {
      setValue(brandList);
    }
  }, [defaultValue, brandList]);

  return (
    <Modal
      rootClassName="redesign modal contract-brands-modal"
      width={800}
      centered
      destroyOnClose
      closeIcon={false}
      open={open}
      onCancel={handleClose}
      footer={[
        <Button key="cancel" className="button-sm secondary" onClick={handleClose} disabled={loading}>
          Отмена
        </Button>,
        <Button key="save" className="button-sm primary" onClick={handleSave} loading={loading}>
          Сохранить
        </Button>,
      ]}
    >
      <div className="mb-6">
        <SearchInput
          auto
          placeholder="Введите название бренда"
          minLength={DEFAULT_VALUE_0}
          onSearch={async (value) => setSearch(value || '')}
        />
      </div>

      {!search.length && (
        <div className="mb-4 contract-brands-modal__check-all">
          <Checkbox
            className="mb-4"
            checked={checkAll}
            indeterminate={indeterminate}
            onChange={(e) => onCheckAllChange(e.target.checked)}
          >
            Все бренды
          </Checkbox>
        </div>
      )}

      <div className="mb-6 contract-brands-modal__scroll-container">
        <Table
          dataSource={listForView.map((brand) => ({ key: brand, brand }))}
          columns={[
            {
              key: 'brand',
              render: (_, { brand }) => {
                const checked = value.find((item) => item === brand);

                return (
                  <Checkbox value={brand} checked={!!checked} onChange={(e) => onChange(e.target.checked, brand)}>
                    {brand}
                  </Checkbox>
                );
              },
            },
          ]}
          showHeader={false}
          pagination={false}
          virtual
          scroll={{ y: 800 }}
        />
      </div>
    </Modal>
  );
};
